// src/utils/league.ts — liigan kausitaulukon laskenta
//
// Taulukkoa ei tallenneta mihinkään. Se lasketaan joka kerta kauden
// päättyneiden iltojen **otteluista** kauden säännöillä. Tuloksen korjaus
// tai säännön muutos näkyy heti, eikä mikään jää epäsynkroniin.
// Suunnitelma ja päätökset: docs/liiga.md.
//
// ── Raakapisteet, 2.11.2026 ──────────────────────────────────
//
// Ennen tätä kausi laskettiin illan **sijoituksesta** asteikolla
// 20·17·15·13 plus läsnäolopiste. Se palkitsi sijoituksesta, ei pelistä:
// 1.11. illassa neljän pisteen ero illan sisällä kutistui yhdeksi
// asteikkoaskeleeksi, ja kaksi ihmistä laski illan eri tavalla kuin appi.
//
// Nyt kausi on **tehtyjen pisteiden summa**: joka pallo lasketaan.
// Voitot ja voittoprosentti seurataan erikseen, mutta ne eivät ratkaise
// sijoitusta — parit vaihtuvat joka kierroksella, joten voitto kertoo
// yhtä paljon parionnesta kuin omasta pelistä.
//
// Pisteet otetaan vain **täysiltä kierroksilta** (completeRotationGames):
// neljällä pelaajalla täysi kierto on 3, 6 tai 9 ottelua. Kesken jäänyt
// kierto toistaa osan pariutuksista eikä ole tasapuolinen. Siksi illan
// oma taulukko ja kausitaulukko voivat poiketa toisistaan, jos kierto jäi
// kesken — `LeagueNight.dropped` kertoo montako ottelua jäi laskennan
// ulkopuolelle, jotta mikään ei putoa hiljaa.
//
// Sijoitus lasketaan samoista otteluista kuin pisteet, ei kantaan
// kirjatusta `finalPosition`-kentästä. Niin taulukon jokainen luku tulee
// samasta lähteestä eikä illan uudelleenlaskennan unohtaminen voi jättää
// kausipisteitä vanhoiksi.

import { ClubEvent, Game, Participant, Season, SeasonRules, rankPlayers, sharedPositions } from "../types";
import { completeRotationGames } from "./americano";

export interface LeagueNight {
  eventId: string;
  datetime: string;
  /** Illan sijoitus, tai null jos pelaaja oli paikalla mutta ei pelannut. */
  position: number | null;
  /** Tehdyt pisteet lasketuilta kierroksilta. */
  points: number;
  /** Vastaan menneet pisteet, pistesaldon toinen puoli. */
  against: number;
  wins: number;
  losses: number;
  /** Lasketut ottelut. */
  games: number;
  /** Kierron ulkopuolelle jääneet ottelut, joita ei laskettu. */
  dropped: number;
  /** Lasketaanko tämä ilta kauden pisteisiin (countBest parasta). */
  counted: boolean;
}

export interface LeagueRow {
  /** playerId, tai nimi pienellä jos osallistujalla ei ole pelaajatunnusta. */
  key: string;
  playerId?: string;
  name: string;
  /** Kauden pisteet: tehdyt pisteet lasketuista illoista. */
  points: number;
  /** Vastaan menneet pisteet lasketuista illoista. */
  against: number;
  /** Pistesaldo, points − against. */
  diff: number;
  wins: number;
  losses: number;
  /** Lasketut ottelut. */
  games: number;
  /** Voittoprosentti 0–100, tai null jos otteluita ei ole. */
  winPct: number | null;
  /** Iltoja joissa oli mukana. */
  played: number;
  /**
   * Iltoja jotka kerryttivät pisteitä (countBest parasta, joista jokin
   * ottelu oli pelattu). Tämä on `pointsPerNight`-jakaja, ei `played`:
   * myöhään kaudella pelattuja iltoja on enemmän kuin laskettuja, ja
   * silloin `played`illä jakaminen antaisi liian pienen keskiarvon.
   */
  countedNights: number;
  /**
   * Pisteitä per laskettu ilta, yhden desimaalin tarkkuudella.
   *
   * Tämä on taulukon toinen sarake pisteiden rinnalla. Se vastaa
   * kysymykseen "kuinka paljon tekee kun on paikalla", johon pelkkä
   * summa ei vastaa: kahdesti poissa ollut jää summassa jälkeen vaikka
   * pelaisi parhaiten. Kauden sijoitusta se ei ratkaise.
   *
   * null jos yksikään ilta ei kerryttänyt pisteitä.
   */
  pointsPerNight: number | null;
  bestPosition: number | null;
  /** Täyttyykö vähimmäismäärä palkintoon (rules.minEvents). */
  eligible: boolean;
  nights: LeagueNight[];
}

export interface LeagueStandings {
  rows: LeagueRow[];
  /** Päättyneet illat, joista pisteitä on kertynyt. */
  playedNights: number;
  /** Kaikki kauden illat, myös tulevat. */
  totalNights: number;
  /** Illat joissa kierto jäi kesken, eli joista otteluita jäi laskematta. */
  partialNights: number;
}

/** Nimi vertailumuodossa. Sama normalisointi kuin muualla: trim + pienet. */
function nameKey(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Nimi → pelaajatunnus, kun se on yksikäsitteinen.
 *
 * Sama ihminen esiintyy kaudella kahdella tavalla: kutsulinkillä
 * liittyneellä osallistujarivillä on `playerId`, kentän laidalla käsin
 * lisätyllä ei ole. Ilman tätä yhdistämistä hän saa **kaksi riviä**, ja
 * kauden pisteet jakautuvat niiden kesken — vika joka ei näy yhdestä
 * illasta vaan kasvaa iltojen myötä.
 *
 * Jos kaksi eri pelaajatunnusta käyttää samaa näyttönimeä, nimeä ei voi
 * liittää kumpaankaan. Silloin arvo on null ja nimelliset rivit jäävät
 * erilleen: väärälle ihmiselle kirjattu piste on pahempi kuin kahtia
 * jäänyt rivi, jonka ylläpitäjä näkee ja osaa korjata.
 *
 * @param byEvent osallistujat tapahtumittain.
 * @param events illat jotka lasketaan.
 * @return nimi → tunnus, tai null jos nimi on monitulkintainen.
 */
function playerIdsByName(
  byEvent: Record<string, Participant[]>,
  events: ClubEvent[],
): Map<string, string | null> {
  const out = new Map<string, string | null>();
  events.forEach(ev => {
    (byEvent[ev.id] ?? []).forEach(p => {
      if (!p.playerId) return;
      const n = nameKey(p.displayName);
      if (!n) return;
      const nyt = out.get(n);
      if (nyt === undefined) out.set(n, p.playerId);
      else if (nyt !== p.playerId) out.set(n, null);
    });
  });
  return out;
}

/**
 * Kauden taulukko iltojen otteluista.
 *
 * @param season kausi, jonka säännöillä lasketaan.
 * @param events klubin tapahtumat; kauden päättyneet illat poimitaan tästä.
 * @param participantsByEvent osallistujat tapahtumittain. Tarvitaan
 *   nimien ja pelaajatunnusten liittämiseen sekä läsnäoloon: paikalla
 *   ollut mutta pelaamaton näkyy taulukossa ilman pisteitä.
 * @param gamesByEvent ottelut tapahtumittain. Pisteet, voitot ja illan
 *   sijoitus lasketaan näistä, ei kantaan kirjatuista kentistä.
 */
export function computeStandings(
  season: Season,
  events: ClubEvent[],
  participantsByEvent: Record<string, Participant[]>,
  gamesByEvent: Record<string, Game[]>,
): LeagueStandings {
  const seasonEvents = events.filter(e => e.seasonId === season.id);
  const finished = seasonEvents.filter(e => e.status === "finished");
  const idByName = playerIdsByName(participantsByEvent, finished);

  const rows = new Map<string, LeagueRow>();
  let partialNights = 0;

  const tyhjaRivi = (key: string, name: string, playerId?: string): LeagueRow => ({
    key, playerId, name, points: 0, against: 0, diff: 0, wins: 0, losses: 0,
    games: 0, winPct: null, played: 0, countedNights: 0, pointsPerNight: null,
    bestPosition: null, eligible: false, nights: [],
  });

  finished.forEach(ev => {
    const parts = participantsByEvent[ev.id] ?? [];
    const split = completeRotationGames(gamesByEvent[ev.id] ?? []);
    if (split.dropped.length > 0) partialNights++;

    // Illan taulukko lasketuista otteluista. Sijoitus tulee samasta
    // laskusta kuin pisteet, joten illan rivit eivät voi olla ristiriidassa.
    const standings = rankPlayers(split.counted);
    const positions = sharedPositions(standings);
    const byName = new Map<string, { pts: number; against: number; wins: number; losses: number; played: number; position: number }>();
    standings.forEach((s, i) => byName.set(nameKey(s.name), {
      pts: s.pts, against: s.ptsAgainst, wins: s.wins, losses: s.losses,
      played: s.played, position: positions[i],
    }));

    // Montako ottelua pelaajalta jäi laskematta. Kierroksen ulkopuolella
    // ollut ottelu voi koskea vain osaa pelaajista (käsin lisätty ottelu),
    // joten tämä lasketaan pelaajittain eikä illan tasolla.
    const droppedFor = new Map<string, number>();
    split.dropped.forEach(g => {
      [...g.team1, ...g.team2].forEach(n => {
        const k = nameKey(n);
        droppedFor.set(k, (droppedFor.get(k) ?? 0) + 1);
      });
    });

    const lisaa = (nimi: string, displayName: string, playerId?: string, attended = false) => {
      const st = byName.get(nimi);
      const played = (st?.played ?? 0) > 0;
      // Sijoitus on vain pelanneilla. Paikalla ollut mutta pelaamaton
      // pääsee mukaan attended-kentällä, mutta ei saa pisteitä: kausi
      // palkitsee tehdyistä pisteistä, ei osallistumisesta.
      if (!played && !attended) return;
      const key = playerId || idByName.get(nimi) || nimi;
      if (!key) return;
      let row = rows.get(key);
      if (!row) { row = tyhjaRivi(key, displayName, playerId); rows.set(key, row); }
      // Tunnus voi tulla vasta myöhemmästä illasta, jos ensimmäinen rivi
      // oli nimellinen.
      if (!row.playerId && playerId) row.playerId = playerId;
      row.nights.push({
        eventId: ev.id,
        datetime: ev.datetime,
        position: played && st ? st.position : null,
        points: st?.pts ?? 0,
        against: st?.against ?? 0,
        wins: st?.wins ?? 0,
        losses: st?.losses ?? 0,
        games: st?.played ?? 0,
        dropped: droppedFor.get(nimi) ?? 0,
        counted: false,
      });
    };

    const nahty = new Set<string>();
    parts.forEach(p => {
      const nimi = nameKey(p.displayName);
      if (!nimi) return;
      nahty.add(nimi);
      lisaa(nimi, p.displayName, p.playerId, p.attended === true);
    });
    // Otteluissa esiintyvä pelaaja, jonka osallistujarivi on poistettu.
    // Hänen pisteensä ovat silti kirjattuja tuloksia, joten ne lasketaan.
    standings.forEach(s => {
      const nimi = nameKey(s.name);
      if (nahty.has(nimi) || s.played === 0) return;
      nahty.add(nimi);
      lisaa(nimi, s.name);
    });
  });

  const result: LeagueRow[] = [];
  rows.forEach(row => {
    // Parhaat illat lasketaan, loput näytetään mutta eivät vaikuta.
    const byPoints = [...row.nights].sort((a, b) =>
      b.points - a.points || (b.points - b.against) - (a.points - a.against));
    byPoints.slice(0, season.rules.countBest).forEach(n => { n.counted = true; });
    row.nights.sort((a, b) => (a.datetime || "").localeCompare(b.datetime || ""));

    const lasketut = row.nights.filter(n => n.counted);
    row.points = lasketut.reduce((s, n) => s + n.points, 0);
    row.against = lasketut.reduce((s, n) => s + n.against, 0);
    row.diff = row.points - row.against;
    row.wins = lasketut.reduce((s, n) => s + n.wins, 0);
    row.losses = lasketut.reduce((s, n) => s + n.losses, 0);
    row.games = lasketut.reduce((s, n) => s + n.games, 0);
    row.winPct = row.games > 0 ? Math.round((row.wins / row.games) * 1000) / 10 : null;
    row.played = row.nights.length;
    // Vain pisteitä kerryttäneet illat ovat jakaja. Pelkkä läsnäolo ei
    // laske keskiarvoa, koska se ei kerrytä pisteitäkään.
    row.countedNights = lasketut.filter(n => n.games > 0).length;
    row.pointsPerNight = row.countedNights > 0
      ? Math.round((row.points / row.countedNights) * 10) / 10
      : null;
    row.bestPosition = row.nights.reduce<number | null>((m, n) =>
      (n.position !== null && (m === null || n.position < m) ? n.position : m), null);
    row.eligible = row.played >= season.rules.minEvents;
    result.push(row);
  });

  // Sama ketju kuin illassa: pisteet, sitten voitot, sitten pistesaldo.
  result.sort((a, b) =>
    b.points - a.points ||
    b.wins - a.wins ||
    b.diff - a.diff ||
    a.name.localeCompare(b.name, "fi")
  );

  return {
    rows: result,
    playedNights: finished.length,
    totalNights: seasonEvents.length,
    partialNights,
  };
}

/** Sääntöteksti kauden luvuista, jotta se ei voi erota laskennasta. */
export function rulesLines(rules: SeasonRules, lang: "fi" | "en"): string[] {
  return lang === "fi" ? [
    "Kauden pisteet ovat tehtyjen pisteiden summa — joka pallo lasketaan",
    "Illan voittaa se joka tekee eniten pisteitä; tasapisteissä ratkaisevat voitot",
    "P/ilta on pisteet jaettuna lasketuilla illoilla — paljonko teet kun olet paikalla",
    "Voitot ja voitto-% näkyvät rivin erittelyssä, mutta eivät ratkaise sijoitusta",
    "Kauteen lasketaan vain täydet kierrokset: neljällä pelaajalla 3, 6 tai 9 ottelua",
    `Kaudesta lasketaan ${rules.countBest} parasta iltaa — poissaolo ei pudota pelistä`,
    `Palkintoihin oikeuttaa vähintään ${rules.minEvents} pelattua iltaa`,
  ] : [
    "Season points are the sum of points you scored — every rally counts",
    "The night goes to whoever scores most; wins break a tie",
    "Pts/n is points divided by counted nights — how much you score when you show up",
    "Wins and win% are in the row detail, but they do not decide the season",
    "Only full rotations count: with four players that is 3, 6 or 9 matches",
    `Best ${rules.countBest} nights count — missing a night does not drop you out`,
    `Prizes require at least ${rules.minEvents} nights played`,
  ];
}
